/**
 * Equipment Tietopohja IA body — Vaikutusketjut / Komponentit / Riippuvuudet.
 *
 * Presentation is type-agnostic (`Kb*` + `kb-ia`). Pass an `EquipmentKbIaAdapter`
 * for the live Virta / Riippuvuudet / path-port projection.
 */
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { ChevronRight } from "lucide-react";
import {
  KB_UNIT_SIGNAL_GROUP_LABEL_FI,
  KB_UNIT_SIGNAL_GROUP_ORDER,
  capabilityCardFromBehavior,
  capabilityCardFromQuantityFamily,
  capabilityCardFromRule,
  chainsForPartFocus,
  filterChainCalcFamiliesForPart,
  filterChainRulesForPart,
  groupByCapabilityLayer,
  pathChainHasPartContent,
  placementFromBehavior,
  proposedRulesForComponent,
  resolveUnitSignalNameFi,
  signalRowFromBoundary,
  signalRowFromDossier,
  signalRowFromUnit,
  KB_LAYER_SECTION_LABEL_FI,
  kbEntityNameFi,
  type KbCapabilityLayer,
  type KbPartFocus,
  type KbDependencyGroup,
  type KbPathChain,
  type KbDossierSignalRow,
  type KbUnitLevelSignal,
  type KbUnitSignalGroup,
} from "@/lib/kb-ia";

import type {
  KBBehaviorDef,
  KBComponent,
  KBCriterionText,
  KBEquipmentType,
} from "@/lib/api/agent";
import { getKBCriteriaText } from "@/lib/api/agent";
import { KbCapabilityCard } from "@/components/admin/KbCapabilityCard";
import { KbSignalRow } from "@/components/admin/KbSignalRow";
import { cn } from "@/lib/utils";
import { cardTonePill } from "@/lib/card-styles";

type KbIaLens = "flow" | "component" | "dependencies";

/** Per-type live projection into the shared IA body. */
export type EquipmentKbIaAdapter = {
  buildPathChains: (
    kb: KBEquipmentType,
    criteriaText: Record<string, KBCriterionText>,
  ) => KbPathChain[];
  buildDependencyGroups: (
    kb: KBEquipmentType,
    criteriaText: Record<string, KBCriterionText>,
  ) => KbDependencyGroup[];
  buildUnitSignals: (kb: KBEquipmentType) => KbUnitLevelSignal[];
  componentAirPaths: (kb: KBEquipmentType) => Record<string, string[]>;
  behaviorIdsUnderComponent: (
    kb: KBEquipmentType,
    componentId: string,
  ) => string[];
  dossierSignalsForComponent: (
    componentId: string,
    owned: {
      id: string;
      name_fi: string;
      unit?: string;
      criticality?: string;
    }[],
    kb: KBEquipmentType,
  ) => KbDossierSignalRow[];
};

type NestedCap = {
  behavior: KBBehaviorDef;
  layer: KbCapabilityLayer;
  alsoUnderLabels: string[];
  pairLabel: string | null;
};

function pushNested(
  map: Map<string, NestedCap[]>,
  parentId: string,
  cap: NestedCap,
) {
  const list = map.get(parentId) ?? [];
  list.push(cap);
  map.set(parentId, list);
}

function CriticalityLegend() {
  return (
    <div className="flex gap-3 text-[10px] text-muted-foreground pt-1">
      <span className="flex items-center gap-1">
        <span className="w-2 h-2 rounded-sm bg-rose-500/20 border border-rose-500/30" />
        vaadittu
      </span>
      <span className="flex items-center gap-1">
        <span className="w-2 h-2 rounded-sm bg-warning-subtle border border-warning/30" />
        suositeltu
      </span>
      <span className="flex items-center gap-1">
        <span className="w-2 h-2 rounded-sm bg-muted border border-border" />
        valinnainen
      </span>
    </div>
  );
}

// Shared selectable-pill button — one visual system (size, radius, selected/unselected
// tokens) for every part-focus chip in this file (lens toggle and component
// focus), so switching lens never changes what "selected" looks like.
function SelectablePill({
  selected,
  onClick,
  children,
}: {
  selected: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors",
        selected
          ? "border-foreground/30 bg-foreground/10 text-foreground"
          : "border-border bg-muted/30 text-muted-foreground hover:text-foreground",
      )}
    >
      {children}
    </button>
  );
}

// Uppercase role-label caption above a pill row (Finnish UI copy) — makes
// each row's semantics explicit so switching Näkymä doesn't silently change what the pills mean.
function PillRowLabel({ children }: { children: ReactNode }) {
  return (
    <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mr-1">
      {children}
    </span>
  );
}

function LensToggle({
  lens,
  onChange,
  showFlow,
  showDependencies,
}: {
  lens: KbIaLens;
  onChange: (l: KbIaLens) => void;
  /** Omit a lens pill entirely when its equipment type has nothing to show
   * (no chains / no dependency groups) — never render it as "0 ketjua". */
  showFlow: boolean;
  showDependencies: boolean;
}) {
  const options = (
    [
      ["flow", "Vaikutusketjut", showFlow],
      ["component", "Komponentit", true],
      ["dependencies", "Riippuvuudet", showDependencies],
    ] as const
  ).filter(([, , show]) => show);

  return (
    <div className="flex flex-wrap items-center gap-2 mb-3">
      <PillRowLabel>Näkymä</PillRowLabel>
      {options.map(([id, label]) => (
        <SelectablePill
          key={id}
          selected={lens === id}
          onClick={() => onChange(id)}
        >
          {label}
        </SelectablePill>
      ))}
    </div>
  );
}

/** Shared part focus for flow + component (selection persists across lenses). */
function PartFocusToggle({
  components,
  focus,
  onChange,
  showUnitOption,
}: {
  components: KBComponent[];
  focus: KbPartFocus;
  onChange: (f: KbPartFocus) => void;
  /** Unit-level caps belong under Komponentit only — not Vaikutusketjut. */
  showUnitOption: boolean;
}) {
  return (
    <div className="flex flex-wrap items-center gap-1.5 mb-3">
      <PillRowLabel>Valitse osa</PillRowLabel>
      <SelectablePill
        selected={focus === "all"}
        onClick={() => onChange("all")}
      >
        Kaikki
      </SelectablePill>
      {showUnitOption && (
        <SelectablePill
          selected={focus === "unit"}
          onClick={() => onChange("unit")}
        >
          Laitteen taso
        </SelectablePill>
      )}
      {components.map((c) => (
        <SelectablePill
          key={c.id}
          selected={focus === c.id}
          onClick={() => onChange(c.id)}
        >
          {c.name_fi}
        </SelectablePill>
      ))}
    </div>
  );
}

function UnitLevelDossier({
  unit,
  components,
  unitSignals,
  criteriaText,
  expanded,
  onToggle,
}: {
  unit: { behavior: KBBehaviorDef; layer: KbCapabilityLayer }[];
  components: KBComponent[];
  unitSignals: KbUnitLevelSignal[];
  criteriaText: Record<string, KBCriterionText>;
  expanded: boolean;
  onToggle: () => void;
}) {
  const signalsByGroup = useMemo(() => {
    const map = new Map<KbUnitSignalGroup, KbUnitLevelSignal[]>();
    for (const g of KB_UNIT_SIGNAL_GROUP_ORDER) map.set(g, []);
    for (const s of unitSignals) {
      map.get(s.group)?.push(s);
    }
    return map;
  }, [unitSignals]);

  return (
    <div
      className={cn(
        "rounded-lg border border-border bg-muted/20 border-l-[3px] transition-colors",
        expanded ? "border-l-accent" : "border-l-transparent",
      )}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className="flex w-full items-start gap-2 px-3 py-2 text-left"
      >
        <ChevronRight
          className={cn(
            "mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform",
            expanded && "rotate-90",
          )}
        />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold">Laitteen taso</p>
          {!expanded && (
            <p className="text-[10px] text-muted-foreground mt-0.5">
              {unitSignals.length} signaalia · {unit.length} kyvykkyyttä
            </p>
          )}
        </div>
      </button>
      {expanded && (
        <div className="px-3 pb-3 space-y-3">
          <p className="text-[11px] text-muted-foreground leading-relaxed">
            Laitteen jaetut signaalit (ulko / tulo / poisto / jäte, asetusarvot,
            virta, paine) — inventaario, ei “kuka tarvitsee”. Kyvykkyydet
            ilmoittavat tarpeen. Vyöhyke/DH: Riippuvuudet.
          </p>

          <div className="border-t border-border/50 pt-2 space-y-3">
            <p className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground/80">
              Signaalit · {unitSignals.length}
            </p>
            <p className="text-[10px] text-muted-foreground mb-1.5 leading-relaxed">
              Equipment-shared inventaario. Komponenttien omat signaalit
              (venttiilit, ΔP, rajapintalämpötilat) näkyvät osan dossierissa.
            </p>
            {KB_UNIT_SIGNAL_GROUP_ORDER.map((group) => {
              const signals = signalsByGroup.get(group) ?? [];
              if (signals.length === 0) return null;
              return (
                <div key={group} className="space-y-0.5">
                  <p className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground/70 mb-1">
                    {KB_UNIT_SIGNAL_GROUP_LABEL_FI[group]} · {signals.length}
                  </p>
                  {signals.map((signal) => (
                    <KbSignalRow
                      key={signal.id}
                      model={signalRowFromUnit(
                        signal,
                        resolveUnitSignalNameFi(signal, components),
                      )}
                    />
                  ))}
                </div>
              );
            })}
          </div>

          <div className="border-t border-border/50 pt-2 space-y-3">
            {unit.length === 0 ? (
              <>
                <p className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground/80 mb-1">
                  Kyvykkyydet · 0
                </p>
                <p className="text-[10px] text-muted-foreground italic">
                  Ei laitteen tason kyvykkyyksiä.
                </p>
              </>
            ) : (
              groupByCapabilityLayer(unit, (c) => c.layer).map(
                ({ layer, items }) => (
                  <div key={layer} className="space-y-1.5">
                    <p className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground/80 mb-1">
                      {KB_LAYER_SECTION_LABEL_FI[layer]} · {items.length}
                    </p>
                    <p className="text-[10px] text-muted-foreground mb-1.5 leading-relaxed">
                      Sijainti: laite. Tarvittavat signaalit + lähde
                      kyvykkyyskortilla.
                    </p>
                    {items.map(({ behavior, layer: itemLayer }) => (
                      <KbCapabilityCard
                        key={behavior.id}
                        model={capabilityCardFromBehavior(
                          behavior,
                          itemLayer,
                          "unit",
                          { criteriaText },
                        )}
                      />
                    ))}
                  </div>
                ),
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ComponentFocusDossier({
  component,
  behaviorById,
  chains,
  airPaths,
  kb,
  criteriaText,
  adapter,
  expanded,
  onToggle,
}: {
  component: KBComponent;
  behaviorById: Map<string, KBBehaviorDef>;
  chains: KbPathChain[];
  airPaths: Record<string, string[]>;
  kb: KBEquipmentType;
  criteriaText: Record<string, KBCriterionText>;
  adapter: EquipmentKbIaAdapter;
  expanded: boolean;
  onToggle: () => void;
}) {
  const pathIds = airPaths[component.id] ?? [];
  const pathLabels = pathIds.map((id) => kbEntityNameFi(kb, id));
  const rules = proposedRulesForComponent(component.id, chains);
  const ruleIds = new Set(rules.map((r) => r.id));
  // Path row wins when the same capability id is also under-component.
  const caps = adapter
    .behaviorIdsUnderComponent(kb, component.id)
    .map((id) => {
      const behavior = behaviorById.get(id);
      if (!behavior) return null;
      const placement = placementFromBehavior(behavior);
      if (!placement || placement.kind === "hide-as-wrapper") {
        return null;
      }
      if (placement.kind === "unit") return null;
      if (ruleIds.has(id)) return null;
      const pairBehavior = placement.pairId
        ? behaviorById.get(placement.pairId)
        : undefined;
      return {
        behavior,
        layer: placement.layer,
        alsoUnderLabels:
          placement.kind === "under-component"
            ? pathLabels.length > 1
              ? pathLabels
              : []
            : [],
        pairLabel: pairBehavior?.name_fi ?? null,
      } satisfies NestedCap;
    })
    .filter((c): c is NestedCap => c !== null);
  const signalRows = [
    ...adapter.dossierSignalsForComponent(
      component.id,
      component.data_points,
      kb,
    ),
    ...quantityFamilyDerivedSignalRows(component),
  ];

  return (
    <div
      className={cn(
        "rounded-lg border border-border bg-muted/20 border-l-[3px] transition-colors",
        expanded ? "border-l-accent" : "border-l-transparent",
      )}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className="flex w-full items-start gap-2 px-3 py-2 text-left"
      >
        <ChevronRight
          className={cn(
            "mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform",
            expanded && "rotate-90",
          )}
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            <p className="text-sm font-semibold">{component.name_fi}</p>
            {component.optional && (
              <span className="rounded px-1 py-0.5 text-[9px] font-medium border border-border text-muted-foreground leading-none">
                valinnainen
              </span>
            )}
          </div>
          {!expanded && (
            <p className="text-[10px] text-muted-foreground mt-0.5">
              {signalRows.length} signaalia · {caps.length + rules.length}{" "}
              kyvykkyyttä
            </p>
          )}
        </div>
      </button>

      {expanded && (
        <div className="px-3 pb-3 space-y-3">
          {component.description_fi && (
            <p className="text-[11px] text-muted-foreground leading-relaxed">
              {component.description_fi}
            </p>
          )}
          <p className="text-[10px] text-muted-foreground">
            Istuu virroissa:{" "}
            {pathLabels.length > 0 ? pathLabels.join(" · ") : "— (ei polkua)"}
            {pathLabels.length > 1 ? " · jaettu / cross-stream" : ""}
          </p>

          <div className="border-t border-border/50 pt-2">
            <p className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground/80 mb-1">
              Signaalit · {signalRows.length}
            </p>
            <p className="text-[10px] text-muted-foreground mb-1.5 leading-relaxed">
              Inventaario — mitä osa omistaa / altistaa. Rajapinnat ↔-vihjeellä
              (yhdistys polun järjestyksestä).{" "}
              <span className="text-foreground/80">laskettu · vaihtoehto</span>{" "}
              = quantity-family taustakanava; tehollinen arvo (kyvykkyydet /
              tehot) valitaan prioriteetilla alla. Kuka tarvitsee:
              kyvykkyyskortilla.
            </p>
            {signalRows.length === 0 ? (
              <p className="text-[10px] text-muted-foreground italic">
                Ei signaaleja.
              </p>
            ) : (
              <div className="space-y-0.5">
                {signalRows.map((row) => (
                  <KbSignalRow key={row.id} model={signalRowFromDossier(row)} />
                ))}
              </div>
            )}
          </div>

          {(component.quantity_families?.length ?? 0) > 0 && (
            <div className="border-t border-border/50 pt-2 space-y-2">
              <p className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground/80 mb-1">
                Laskennat · {component.quantity_families!.length}
              </p>
              <p className="text-[10px] text-muted-foreground mb-1.5 leading-relaxed">
                Quantity family — sama kyvykkyyskortin muoto. Määrittelee miten
                tehollinen suure syntyy (prioriteetti + lauseke). Kyvykkyydet
                käyttävät tehollista arvoa.
              </p>
              <div className="space-y-1.5">
                {component.quantity_families!.map((family) => (
                  <KbCapabilityCard
                    key={family.id}
                    model={capabilityCardFromQuantityFamily(family)}
                  />
                ))}
              </div>
            </div>
          )}

          {(caps.length > 0 || rules.length > 0) &&
            groupByCapabilityLayer(
              [
                ...rules.map((rule) => ({
                  kind: "rule" as const,
                  layer: rule.layer,
                  rule,
                })),
                ...caps.map((cap) => ({
                  kind: "cap" as const,
                  layer: cap.layer,
                  cap,
                })),
              ],
              (item) => item.layer,
            ).map(({ layer, items }) => (
              <div
                key={layer}
                className="border-t border-border/50 pt-2 space-y-2"
              >
                <p className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground/80 mb-1">
                  {KB_LAYER_SECTION_LABEL_FI[layer]} · {items.length}
                </p>
                <p className="text-[10px] text-muted-foreground mb-1.5 leading-relaxed">
                  Sama kyvykkyyskortti (havainto / selitys / toimenpide). Home =
                  komponentti.
                </p>
                <div className="space-y-1.5">
                  {items.map((item) =>
                    item.kind === "rule" ? (
                      <KbCapabilityCard
                        key={item.rule.id}
                        model={capabilityCardFromRule(
                          item.rule,
                          behaviorById,
                          "component",
                        )}
                      />
                    ) : (
                      <KbCapabilityCard
                        key={item.cap.behavior.id}
                        model={capabilityCardFromBehavior(
                          item.cap.behavior,
                          item.cap.layer,
                          "component",
                          {
                            shared_labels_fi: item.cap.alsoUnderLabels,
                            pair_label_fi: item.cap.pairLabel,
                            criteriaText,
                          },
                        )}
                      />
                    ),
                  )}
                </div>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}

export function KbEquipmentIaBody({
  kb,
  adapter,
}: {
  kb: KBEquipmentType;
  adapter: EquipmentKbIaAdapter;
}) {
  // Lazy initializer: kb/adapter are fixed for this component's lifetime (the
  // parent remounts via `key={equipmentType}`), so the default lens can be
  // decided once, synchronously, from whether this type has any chains at all —
  // no flash of an empty "Vaikutusketjut" lens before falling back.
  const [lens, setLens] = useState<KbIaLens>(() =>
    adapter.buildPathChains(kb, {}).length > 0 ? "flow" : "component",
  );
  const [partFocus, setPartFocus] = useState<KbPartFocus>("all");
  const [criteriaText, setCriteriaText] = useState<
    Record<string, KBCriterionText>
  >({});

  const liveChains = useMemo(
    () => adapter.buildPathChains(kb, criteriaText),
    [adapter, kb, criteriaText],
  );
  const liveDependencyGroups = useMemo(
    () => adapter.buildDependencyGroups(kb, criteriaText),
    [adapter, kb, criteriaText],
  );
  const unitSignals = useMemo(
    () => adapter.buildUnitSignals(kb),
    [adapter, kb],
  );
  const airPaths = useMemo(() => adapter.componentAirPaths(kb), [adapter, kb]);

  const [expandedChainIds, setExpandedChainIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [expandedDepIds, setExpandedDepIds] = useState<Set<string>>(
    () => new Set(),
  );

  useEffect(() => {
    getKBCriteriaText()
      .then(setCriteriaText)
      .catch(() => setCriteriaText({}));
  }, []);

  const behaviorById = useMemo(() => {
    const map = new Map<string, KBBehaviorDef>();
    for (const b of kb.behaviors) map.set(b.id, b);
    return map;
  }, [kb.behaviors]);

  const grouped = useMemo(() => {
    const byComponent = new Map<string, NestedCap[]>();
    const byChain = new Map<string, NestedCap[]>();
    const unit: { behavior: KBBehaviorDef; layer: KbCapabilityLayer }[] = [];
    let placed = 0;

    const parentLabel = (id: string) => {
      const component = kb.components.find((c) => c.id === id);
      if (component) return component.name_fi;
      return kbEntityNameFi(kb, id);
    };

    for (const behavior of kb.behaviors) {
      const placement = placementFromBehavior(behavior);
      if (!placement) {
        unit.push({ behavior, layer: "monitoring" });
        placed += 1;
        continue;
      }
      if (placement.kind === "hide-as-wrapper") continue;
      placed += 1;
      if (placement.kind === "unit") {
        unit.push({ behavior, layer: placement.layer });
        continue;
      }

      const pairBehavior = placement.pairId
        ? behaviorById.get(placement.pairId)
        : undefined;
      const pairLabel = pairBehavior?.name_fi ?? null;

      if (placement.kind === "under-component") {
        for (const parentId of placement.underIds) {
          const alsoUnderLabels = placement.underIds
            .filter((id) => id !== parentId)
            .map(parentLabel);
          const cap: NestedCap = {
            behavior,
            layer: placement.layer,
            alsoUnderLabels,
            pairLabel,
          };
          const paths = airPaths[parentId] ?? [];
          for (const pathId of paths) {
            const also = paths.filter((id) => id !== pathId).map(parentLabel);
            pushNested(byChain, pathId, {
              ...cap,
              alsoUnderLabels: also.length > 0 ? also : alsoUnderLabels,
            });
          }
          if (paths.length === 0) {
            pushNested(byComponent, parentId, cap);
          }
        }
        continue;
      }

      for (const parentId of placement.underIds) {
        const alsoUnderLabels = placement.underIds
          .filter((id) => id !== parentId)
          .map(parentLabel);
        pushNested(byChain, parentId, {
          behavior,
          layer: placement.layer,
          alsoUnderLabels,
          pairLabel,
        });
      }
    }

    return { byComponent, byChain, unit, placed };
  }, [kb, behaviorById, airPaths]);

  // Path lens never uses unit focus — coerce before filtering chains.
  const flowPartFocus: KbPartFocus = partFocus === "unit" ? "all" : partFocus;

  const pathChains = useMemo(() => {
    const chains = chainsForPartFocus(flowPartFocus, liveChains, airPaths);
    // Component filter: drop chains with no matching relationship rules
    // or stage calc cards (topology membership alone can leave empty groups).
    if (flowPartFocus === "all") return chains;
    return chains.filter((c) => pathChainHasPartContent(c, flowPartFocus));
  }, [flowPartFocus, liveChains, airPaths]);

  const pathChainIdKey = pathChains.map((c) => c.id).join(",");
  const depGroupIdKey = liveDependencyGroups.map((g) => g.id).join(",");

  // Sync open state when the visible set or part-focus changes — never when the
  // user collapses (do not watch expanded*.size; that fought toggle/collapse).
  useEffect(() => {
    const ids = pathChainIdKey ? pathChainIdKey.split(",") : [];
    if (ids.length === 0) {
      setExpandedChainIds(new Set());
      return;
    }
    if (flowPartFocus !== "all") {
      setExpandedChainIds(new Set(ids));
      return;
    }
    setExpandedChainIds((prev) => {
      const stillOpen = ids.filter((id) => prev.has(id));
      if (stillOpen.length > 0) return new Set(stillOpen);
      return new Set([ids[0]!]);
    });
  }, [flowPartFocus, pathChainIdKey]);

  useEffect(() => {
    const ids = depGroupIdKey ? depGroupIdKey.split(",") : [];
    if (ids.length === 0) {
      setExpandedDepIds(new Set());
      return;
    }
    setExpandedDepIds((prev) => {
      const stillOpen = ids.filter((id) => prev.has(id));
      if (stillOpen.length > 0) return new Set(stillOpen);
      return new Set([ids[0]!]);
    });
  }, [depGroupIdKey]);

  const toggleChain = (id: string) =>
    setExpandedChainIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const expandAllChains = () =>
    setExpandedChainIds(new Set(pathChains.map((c) => c.id)));

  const collapseAllChains = () => setExpandedChainIds(new Set());

  const toggleDep = (id: string) =>
    setExpandedDepIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const expandAllDeps = () =>
    setExpandedDepIds(new Set(liveDependencyGroups.map((g) => g.id)));

  const collapseAllDeps = () => setExpandedDepIds(new Set());

  const setLensSafe = (next: KbIaLens) => {
    // Unit-level focus is Komponentti-only; leave it behind when opening path lens.
    if (next === "flow" && partFocus === "unit") {
      setPartFocus("all");
    }
    setLens(next);
  };

  const showPartFilter = lens === "flow" || lens === "component";
  const showFlowLens = liveChains.length > 0;
  const showDependenciesLens = liveDependencyGroups.length > 0;

  return (
    <>
      <LensToggle
        lens={lens}
        onChange={setLensSafe}
        showFlow={showFlowLens}
        showDependencies={showDependenciesLens}
      />
      <p className="text-[10px] text-muted-foreground mb-3 leading-relaxed -mt-1">
        Signaalit = inventaario (osa, laitteen jaettu, tai rajan yli);
        kyvykkyydet = kuka tarvitsee mitä (komponentti · polku · laite ·
        riippuvuus). Kolme lukutapaa:{" "}
        <span className="font-medium text-foreground/80">Vaikutusketjut</span> =
        prosessi kanavassa;{" "}
        <span className="font-medium text-foreground/80">Komponentit</span> =
        osan dossier;{" "}
        <span className="font-medium text-foreground/80">Riippuvuudet</span> =
        vastapuolen dossier (vyöhyke / rakennus / laite).{" "}
        <span className="font-medium text-foreground/80">Laitteen taso</span>{" "}
        vain Komponentit-näkymässä.
      </p>

      {showPartFilter && (
        <PartFocusToggle
          components={kb.components}
          focus={lens === "flow" ? flowPartFocus : partFocus}
          onChange={setPartFocus}
          showUnitOption={lens === "component"}
        />
      )}

      {lens === "flow" && (
        <FlowChainsSection
          chains={pathChains}
          partFocus={flowPartFocus}
          expandedChainIds={expandedChainIds}
          onToggleChain={toggleChain}
          onExpandAll={expandAllChains}
          onCollapseAll={collapseAllChains}
          behaviorById={behaviorById}
        />
      )}

      {lens === "component" && (
        <ComponentLensBody
          kb={kb}
          partFocus={partFocus}
          unit={grouped.unit}
          behaviorById={behaviorById}
          unitSignals={unitSignals}
          chains={liveChains}
          airPaths={airPaths}
          criteriaText={criteriaText}
          adapter={adapter}
        />
      )}

      {lens === "dependencies" && (
        <DependenciesSection
          groups={liveDependencyGroups}
          behaviorById={behaviorById}
          expandedDepIds={expandedDepIds}
          onToggleDep={toggleDep}
          onExpandAll={expandAllDeps}
          onCollapseAll={collapseAllDeps}
        />
      )}

      <p className="text-[10px] text-muted-foreground border-t border-border/50 pt-3">
        Näkyvyys: {grouped.placed}/{kb.behaviors.length} kyvykkyyttä sijoitettu
        (uniikki KB-id).
      </p>
      <CriticalityLegend />
    </>
  );
}

function ComponentLensBody({
  kb,
  partFocus,
  unit,
  behaviorById,
  unitSignals,
  chains,
  airPaths,
  criteriaText,
  adapter,
}: {
  kb: KBEquipmentType;
  partFocus: KbPartFocus;
  unit: { behavior: KBBehaviorDef; layer: KbCapabilityLayer }[];
  behaviorById: Map<string, KBBehaviorDef>;
  unitSignals: KbUnitLevelSignal[];
  chains: KbPathChain[];
  airPaths: Record<string, string[]>;
  criteriaText: Record<string, KBCriterionText>;
  adapter: EquipmentKbIaAdapter;
}) {
  const UNIT_GROUP_ID = "unit";

  const groupIds = useMemo(() => {
    if (partFocus === "unit") return [UNIT_GROUP_ID];
    if (partFocus === "all") {
      return [UNIT_GROUP_ID, ...kb.components.map((c) => c.id)];
    }
    return kb.components.some((c) => c.id === partFocus) ? [partFocus] : [];
  }, [partFocus, kb.components]);

  const groupIdKey = groupIds.join(",");

  const [expandedIds, setExpandedIds] = useState<Set<string>>(
    () => new Set(groupIds[0] ? [groupIds[0]] : []),
  );

  // Kaikki → one open (browse). Single osa / laitteen taso → expand that group.
  useEffect(() => {
    if (groupIds.length === 0) {
      setExpandedIds(new Set());
      return;
    }
    if (partFocus === "all") {
      setExpandedIds((prev) => {
        const stillOpen = groupIds.filter((id) => prev.has(id));
        if (stillOpen.length > 0) return new Set([stillOpen[0]!]);
        return new Set([groupIds[0]!]);
      });
      return;
    }
    setExpandedIds(new Set(groupIds));
  }, [partFocus, groupIdKey, groupIds]);

  const toggleGroup = (id: string) =>
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const expandAll = () => setExpandedIds(new Set(groupIds));
  const collapseAll = () => setExpandedIds(new Set());

  const allExpanded =
    groupIds.length > 0 && groupIds.every((id) => expandedIds.has(id));
  const noneExpanded = expandedIds.size === 0;

  const showUnit = partFocus === "all" || partFocus === "unit";
  const components =
    partFocus === "all"
      ? kb.components
      : partFocus === "unit"
        ? []
        : kb.components.filter((c) => c.id === partFocus);

  if (groupIds.length === 0) {
    return (
      <p className="text-[11px] text-muted-foreground italic">
        Ei komponentteja.
      </p>
    );
  }

  return (
    <div>
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          Komponentit · {groupIds.length} ryhmää
          <span className="normal-case tracking-normal font-normal text-muted-foreground/80">
            {partFocus !== "all" ? " · suodatettu osalla" : ""}
          </span>
        </p>
        {groupIds.length > 1 && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={expandAll}
              disabled={allExpanded}
              className="text-[10px] text-muted-foreground hover:text-foreground disabled:opacity-40 disabled:pointer-events-none underline-offset-2 hover:underline"
            >
              Laajenna kaikki
            </button>
            <span className="text-[10px] text-muted-foreground/50">·</span>
            <button
              type="button"
              onClick={collapseAll}
              disabled={noneExpanded}
              className="text-[10px] text-muted-foreground hover:text-foreground disabled:opacity-40 disabled:pointer-events-none underline-offset-2 hover:underline"
            >
              Supista kaikki
            </button>
          </div>
        )}
      </div>
      <p className="text-[10px] text-muted-foreground mb-2 leading-relaxed">
        Osan dossier: signaalit, kyvykkyydet ja säännöt. Sama avaa/sulje-malli
        kuin Vaikutusketjuissa.
      </p>
      <div className="space-y-2">
        {showUnit && (
          <UnitLevelDossier
            unit={unit}
            components={kb.components}
            unitSignals={unitSignals}
            criteriaText={criteriaText}
            expanded={expandedIds.has(UNIT_GROUP_ID)}
            onToggle={() => toggleGroup(UNIT_GROUP_ID)}
          />
        )}
        {components.map((c) => (
          <ComponentFocusDossier
            key={c.id}
            component={c}
            behaviorById={behaviorById}
            chains={chains}
            airPaths={airPaths}
            kb={kb}
            criteriaText={criteriaText}
            adapter={adapter}
            expanded={expandedIds.has(c.id)}
            onToggle={() => toggleGroup(c.id)}
          />
        ))}
      </div>
    </div>
  );
}

/** Kairos-minted quantity-family channels — inventory siblings, not the effective binding. */
function quantityFamilyDerivedSignalRows(
  component: KBComponent,
): KbDossierSignalRow[] {
  const ownedIds = new Set(component.data_points.map((dp) => dp.id));
  const rows: KbDossierSignalRow[] = [];
  for (const family of component.quantity_families ?? []) {
    const priority = family.priority.join(" → ");
    for (const est of family.estimators) {
      if (est.kind === "measured") continue;
      if (ownedIds.has(est.data_point_id)) continue;
      const needs = [
        ...(est.requires_config ?? []),
        ...(est.requires_learned ?? []),
      ];
      const unlock =
        needs.length > 0
          ? `Avautuu kun: ${needs.join(", ")} (+ lausekkeen lähdesignaalit).`
          : est.expression
            ? `Lauseke: ${est.expression}`
            : undefined;
      rows.push({
        id: est.data_point_id,
        name_fi:
          est.name_fi || family.name_fi || family.name_en || est.data_point_id,
        kind: "owned",
        unit: est.unit || family.unit || undefined,
        criticality: "optional",
        meta_fi:
          est.kind === "accepted"
            ? "hyväksytty · vaihtoehto"
            : "laskettu · vaihtoehto",
        meta_title:
          `Quantity family ${family.id}: tehollinen arvo valitaan prioriteetilla ${priority}. ` +
          "Taustakanava — ei korvaa mitattua, kun mitattu on valmis.",
        note_fi: unlock,
      });
    }
  }
  return rows;
}

function FlowChainsSection({
  chains,
  partFocus,
  expandedChainIds,
  onToggleChain,
  onExpandAll,
  onCollapseAll,
  behaviorById,
}: {
  chains: KbPathChain[];
  partFocus: KbPartFocus;
  expandedChainIds: Set<string>;
  onToggleChain: (chainId: string) => void;
  onExpandAll: () => void;
  onCollapseAll: () => void;
  behaviorById: Map<string, KBBehaviorDef>;
}) {
  if (chains.length === 0) {
    return (
      <p className="text-[11px] text-muted-foreground italic">
        Ei vaikutusketjuja tälle osalle.
      </p>
    );
  }

  const allExpanded =
    chains.length > 0 && chains.every((c) => expandedChainIds.has(c.id));
  const noneExpanded = expandedChainIds.size === 0;

  return (
    <div>
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          Vaikutusketjut · {chains.length} ketjua
          <span className="normal-case tracking-normal font-normal text-muted-foreground/80">
            {" "}
            · lämpö-, paine- ja tehovirrat
            {partFocus !== "all" ? " · suodatettu osalla" : ""}
          </span>
        </p>
        {chains.length > 1 && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onExpandAll}
              disabled={allExpanded}
              className="text-[10px] text-muted-foreground hover:text-foreground disabled:opacity-40 disabled:pointer-events-none underline-offset-2 hover:underline"
            >
              Laajenna kaikki
            </button>
            <span className="text-[10px] text-muted-foreground/50">·</span>
            <button
              type="button"
              onClick={onCollapseAll}
              disabled={noneExpanded}
              className="text-[10px] text-muted-foreground hover:text-foreground disabled:opacity-40 disabled:pointer-events-none underline-offset-2 hover:underline"
            >
              Supista kaikki
            </button>
          </div>
        )}
      </div>
      <p className="text-[10px] text-muted-foreground mb-2 leading-relaxed">
        Anatomia + kyvykkyydet. Otsikko ={" "}
        <span className="font-medium text-foreground/80">
          komento → odotettu muutos
        </span>
        ; tekstissä{" "}
        <span className="font-medium text-foreground/80">
          havainto · selitys · toimenpide
        </span>{" "}
        kun kortti on retoned. Osasuodatin avaa osumaketjut automaattisesti.
      </p>
      <div className="space-y-2">
        {chains.map((chain) => {
          const rules = filterChainRulesForPart(
            chain.proposed_rules,
            partFocus,
          );
          const calcs = filterChainCalcFamiliesForPart(
            chain.calc_families ?? [],
            partFocus,
          );
          const isExpanded = expandedChainIds.has(chain.id);
          const contentCount = rules.length + calcs.length;

          return (
            <div
              key={chain.id}
              className={cn(
                "rounded-lg border border-border bg-muted/20 border-l-[3px] transition-colors",
                isExpanded ? "border-l-accent" : "border-l-transparent",
              )}
            >
              <button
                type="button"
                onClick={() => onToggleChain(chain.id)}
                aria-expanded={isExpanded}
                className="flex w-full items-start gap-2 px-3 py-2 text-left"
              >
                <ChevronRight
                  className={cn(
                    "mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform",
                    isExpanded && "rotate-90",
                  )}
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <p className="text-xs font-medium">{chain.name_fi}</p>
                    {chain.source === "spike-gap" && (
                      <span
                        className={cn(
                          "rounded-full border px-1.5 py-0.5 text-[9px] font-medium leading-none",
                          cardTonePill.warn,
                        )}
                      >
                        ei vielä KB:ssä
                      </span>
                    )}
                  </div>
                  {!isExpanded && (
                    <p className="text-[10px] text-muted-foreground mt-0.5">
                      {contentCount === 0
                        ? "ei kyvykkyyksiä"
                        : [
                            rules.length > 0
                              ? `${rules.length} kyvykkyyttä`
                              : null,
                            calcs.length > 0
                              ? `${calcs.length} laskentaa`
                              : null,
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                    </p>
                  )}
                </div>
              </button>

              {isExpanded && (
                <div className="px-3 pb-2.5">
                  <p className="text-[11px] text-muted-foreground leading-relaxed">
                    {chain.description_fi}
                  </p>
                  <div className="mt-2 flex flex-wrap items-center gap-1">
                    {chain.steps.map((step, i) => (
                      <span key={`${chain.id}-${i}`} className="contents">
                        {i > 0 && (
                          <span className="text-[10px] text-muted-foreground px-0.5">
                            →
                          </span>
                        )}
                        <span className="rounded px-1.5 py-0.5 text-[10px] border border-border bg-muted/40 text-muted-foreground">
                          {step.label_fi}
                        </span>
                      </span>
                    ))}
                  </div>

                  {calcs.length > 0 && (
                    <div className="mt-2 space-y-1.5 border-t border-border/50 pt-2">
                      <p className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground/80 mb-1">
                        Laskennat · {calcs.length}
                      </p>
                      <p className="text-[10px] text-muted-foreground mb-1.5 leading-relaxed">
                        Vaihetehot ja teholliset suureet tällä vaikutusketjulla
                        — sama kortti kuin Komponentit → Laskennat.
                      </p>
                      {calcs.map(({ family, component_id }) => (
                        <KbCapabilityCard
                          key={`${component_id}:${family.id}`}
                          model={capabilityCardFromQuantityFamily(family)}
                        />
                      ))}
                    </div>
                  )}

                  {rules.length > 0 &&
                    groupByCapabilityLayer(rules, (r) => r.layer).map(
                      ({ layer, items }) => (
                        <div
                          key={layer}
                          className="mt-2 space-y-1.5 border-t border-border/50 pt-2"
                        >
                          <p className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground/80 mb-1">
                            {KB_LAYER_SECTION_LABEL_FI[layer]} · {items.length}
                          </p>
                          {items.map((rule) => (
                            <KbCapabilityCard
                              key={rule.id}
                              model={capabilityCardFromRule(
                                rule,
                                behaviorById,
                                "path",
                              )}
                            />
                          ))}
                        </div>
                      ),
                    )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DependenciesSection({
  groups,
  behaviorById,
  expandedDepIds,
  onToggleDep,
  onExpandAll,
  onCollapseAll,
}: {
  groups: KbDependencyGroup[];
  behaviorById: Map<string, KBBehaviorDef>;
  expandedDepIds: Set<string>;
  onToggleDep: (id: string) => void;
  onExpandAll: () => void;
  onCollapseAll: () => void;
}) {
  const totalRules = groups.reduce((n, g) => n + g.rules.length, 0);
  const allExpanded =
    groups.length > 0 && groups.every((g) => expandedDepIds.has(g.id));
  const noneExpanded = expandedDepIds.size === 0;

  if (groups.length === 0) {
    return (
      <p className="text-[11px] text-muted-foreground italic">
        Ei riippuvuuksia tälle laitetyypille.
      </p>
    );
  }

  return (
    <div>
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          Riippuvuudet · {groups.length} vastapuolta
          <span className="normal-case tracking-normal font-normal text-muted-foreground/80">
            {" "}
            · {totalRules} kyvykkyyttä
          </span>
        </p>
        {groups.length > 1 && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onExpandAll}
              disabled={allExpanded}
              className="text-[10px] text-muted-foreground hover:text-foreground disabled:opacity-40 disabled:pointer-events-none underline-offset-2 hover:underline"
            >
              Laajenna kaikki
            </button>
            <span className="text-[10px] text-muted-foreground/50">·</span>
            <button
              type="button"
              onClick={onCollapseAll}
              disabled={noneExpanded}
              className="text-[10px] text-muted-foreground hover:text-foreground disabled:opacity-40 disabled:pointer-events-none underline-offset-2 hover:underline"
            >
              Supista kaikki
            </button>
          </div>
        )}
      </div>
      <p className="text-[10px] text-muted-foreground mb-3 leading-relaxed">
        Sama malli kuin Komponenteissa: vastapuolen dossier ={" "}
        <span className="font-medium text-foreground/80">signaalit</span> (rajan
        inventaario) +{" "}
        <span className="font-medium text-foreground/80">kyvykkyydet</span>{" "}
        (havainto · selitys · toimenpide). Ei erillistä “kytkentäobjektia”.
      </p>
      <div className="space-y-2">
        {groups.map((group) => {
          const isExpanded = expandedDepIds.has(group.id);
          return (
            <div
              key={group.id}
              className={cn(
                "rounded-lg border border-border bg-muted/20 border-l-[3px] transition-colors",
                isExpanded ? "border-l-accent" : "border-l-transparent",
              )}
            >
              <button
                type="button"
                onClick={() => onToggleDep(group.id)}
                aria-expanded={isExpanded}
                className="flex w-full items-start gap-2 px-3 py-2 text-left"
              >
                <ChevronRight
                  className={cn(
                    "mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform",
                    isExpanded && "rotate-90",
                  )}
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <p className="text-sm font-semibold">{group.name_fi}</p>
                    <span
                      className={cn(
                        "shrink-0 rounded-full border px-1.5 py-0.5 text-[9px] font-medium leading-none",
                        cardTonePill.empty,
                      )}
                    >
                      {group.counterpart_type}
                    </span>
                  </div>
                  {!isExpanded && (
                    <p className="text-[10px] text-muted-foreground mt-0.5">
                      {group.signals.length} signaalia · {group.rules.length}{" "}
                      kyvykkyyttä
                    </p>
                  )}
                </div>
              </button>

              {isExpanded && (
                <div className="px-3 pb-3 space-y-3">
                  <p className="text-[11px] text-muted-foreground leading-relaxed">
                    {group.description_fi}
                  </p>

                  <div className="border-t border-border/50 pt-2">
                    <p className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground/80 mb-1">
                      Signaalit · {group.signals.length}
                    </p>
                    <p className="text-[10px] text-muted-foreground mb-1.5 leading-relaxed">
                      Rajan inventaario (IV-kone / vastapuoli). Kuka tarvitsee:
                      kyvykkyyskortilla.
                    </p>
                    {group.signals.map((signal) => (
                      <KbSignalRow
                        key={signal.id}
                        model={signalRowFromBoundary(signal)}
                      />
                    ))}
                  </div>

                  <div className="border-t border-border/50 pt-2 space-y-3">
                    {group.rules.length === 0 ? (
                      <p className="text-[10px] text-muted-foreground italic">
                        Ei kyvykkyyksiä.
                      </p>
                    ) : (
                      groupByCapabilityLayer(group.rules, (r) => r.layer).map(
                        ({ layer, items }) => (
                          <div key={layer} className="space-y-1.5">
                            <p className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground/80 mb-1">
                              {KB_LAYER_SECTION_LABEL_FI[layer]} ·{" "}
                              {items.length}
                            </p>
                            {items.map((rule) => (
                              <KbCapabilityCard
                                key={rule.id}
                                model={capabilityCardFromRule(
                                  rule,
                                  behaviorById,
                                  "dependency",
                                )}
                              />
                            ))}
                          </div>
                        ),
                      )
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
